import * as React from "react"
import parse from "html-react-parser"
import { useRecoilValue, useSetRecoilState } from "recoil"
import { brandingSelector } from "../../recoil/atoms/room"
import {
  useToast,
  UseToastOptions,
  Box,
  Text,
  ToastPosition,
  Flex,
  ToastId
} from "@chakra-ui/react"
import { FaInfoCircle } from "react-icons/fa"
import { IoClose, IoWarning } from "react-icons/io5"
import { Icon, CommonIconProps } from "../../components/common/icons"
import { toastActiveSelector } from "../../recoil/atoms/collapseButton"
import { useTranslation } from "react-i18next"
import { blendWithWhiteBackground } from "../../util"

export interface ToastConfigProps {
  position?: ToastPosition
  wrapperStyles?: React.CSSProperties
  titleStyles?: React.CSSProperties
  descriptionStyles?: React.CSSProperties
  toastOptions?: UseToastOptions
  styleCrossIcon?: CommonIconProps
}

export type showToastMessageProps = (
  toastTitle: string,
  toastDescription?: string,
  options?: {
    type?: "INFO" | "WARNING",
    position?: ToastConfigProps["position"],
    duration?: number
  }
) => ToastId

export const useCustomToast = (
  config?: ToastConfigProps
): showToastMessageProps => {
  const roomStyles = useRecoilValue(brandingSelector)
  const setActiveToast = useSetRecoilState(toastActiveSelector)
  const { t } = useTranslation()

  const roomStylesRef = React.useRef(roomStyles)
  roomStylesRef.current = roomStyles

  const configRef = React.useRef(config)
  const toast = useToast(config?.toastOptions)
  configRef.current = config

  const handleCloseTost = React.useCallback(() => {
    toast.closeAll()
  }, [toast])

  const showToastMessage: showToastMessageProps = React.useCallback(
    (toastTitle, toastDescription?, options?) => {
      const { type, position, duration } = options ?? {}
      
      const toastId = toast({
        title: toastTitle,
        variant: "subtle",
        position: position ?? configRef.current?.position ?? "bottom-right",
        duration: duration ?? 3000,
        isClosable: true,
        onCloseComplete: () => {
          setActiveToast({ isActive: false, toastId: toastId.toString() })
        },
        render: () => (
          <Box
            position="relative"
            display="flex"
            alignItems="center"
            borderRadius="0.375rem"
            color={roomStylesRef.current?.servicePanelElementPrimaryText ?? "white"}
            p={3}
            bg={
              blendWithWhiteBackground(
                roomStylesRef.current?.servicePanelElementModalBackground
              ) ?? "#e9ecef"
            }
            border={
              roomStylesRef.current?.servicePanelElementBorder
                ? `2px solid ${roomStylesRef.current?.servicePanelElementBorder}`
                : "2px solid #ced4da"
            }
            style={config?.wrapperStyles}
          >
            <Text alignSelf="baseline" paddingTop="0.5">
              <Icon
                src={type === "WARNING" ? IoWarning : FaInfoCircle}
                marginRight={3}
              />
            </Text>

            <Flex flexDirection="column">
              <Text marginRight={3} style={config?.titleStyles}>
                {t(toastTitle)}
              </Text>
              {toastDescription && (
                <Text marginRight={3} style={config?.descriptionStyles}>
                  {parse(toastDescription)}
                </Text>
              )}
            </Flex>
            <Icon
              src={IoClose}
              color={
                roomStylesRef.current?.servicePanelElementPrimaryText &&
                roomStylesRef.current?.servicePanelElementPrimaryText
              }
              onClick={handleCloseTost}
              cursor={"pointer"}
              opacity={0.75}
              position="absolute"
              top="3px"
              right="2px"
              {...config?.styleCrossIcon}
            />
          </Box>
        )
      })
      setActiveToast({ isActive: true, toastId: toastId.toString() })
      return toastId
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  return showToastMessage
}
